import { Button, useToast } from '@chakra-ui/react'
import type { AxiosError } from 'axios'
import { PageAlert } from 'BrokerTsLib/src/components'
import type { EditOrganizationUnit } from 'BrokerTsLib/src/dtos'
import {
  useGetOrganizationUnitById,
  useGetOrganizationUnitProperties,
  useUpdateOrganizationUnit,
} from 'BrokerTsLib/src/hooks/useOrganizationUnit'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { OrganizationForm } from '@/containers/organization/OrganizationForm'
import { OrganizationHeader } from '@/containers/organization/OrganizationHeader'
import { SidebarProvider } from '@/context/SidebarContext'
import { Refresh } from '@/icons'
import { PageWrapper } from '@/layouts/components/PageWrapper'
import { Sidebar } from '@/layouts/components/Sidebar'
import type { BreadcrumbItems } from 'BrokerTsLib/src/containers/Breadcrumbs'
import { Breadcrumbs } from 'BrokerTsLib/src/containers/Breadcrumbs'

export const OrganizationEditPage = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const { organizationUnitId: organizationUnitIdParam } = useParams()
  const organizationUnitId = Number(organizationUnitIdParam)

  const {
    data: organizationData,
    refetch: refetchOrganization,
    isLoading: isLoadingOrganization,
    isError: isErrorOrganization,
  } = useGetOrganizationUnitById(organizationUnitId)
  const {
    data: propertiesData,
    refetch: refetchProperties,
    isLoading: isLoadingProperties,
    isError: isErrorProperties,
  } = useGetOrganizationUnitProperties(organizationUnitId)
  const { mutateAsync: update } = useUpdateOrganizationUnit()

  const onSubmit = (values: EditOrganizationUnit) =>
    update(values)
      .then((response) => {
        navigate('/organization/' + values.id)

        return response.data
      })
      .catch((e: AxiosError) => {
        toast({
          title: 'Chyba při ukládání organizace',
          description: `${e.response?.status}: ${e.response?.statusText}`,
          status: 'error',
        })

        return e
      })

  const isLoading = isLoadingOrganization || isLoadingProperties
  const isError = isErrorOrganization || isErrorProperties
  const refetch = () => {
    refetchOrganization()
    refetchProperties()
  }

  const breadcrumbs = useMemo(() => {
    const items: BreadcrumbItems = [
      { to: '/organization/list', label: 'Seznam organizací' },
    ]

    if (organizationData?.data.superiorOrganizationUnitId)
      items.push({
        to: `/organization/${organizationData?.data.superiorOrganizationUnitId}`,
        label:
          organizationData?.data.superiorOrganizationUnitName ??
          'Nadřízená organizace',
      })

    items.push({
      to: `/organization/${organizationUnitId}`,
      label: organizationData?.data.name ?? 'Organizace',
    })

    items.push({
      to: `/organization/${organizationUnitId}/edit`,
      label: 'Upravit',
      current: true,
    })

    return items
  }, [organizationData])

  return isError ? (
    <PageAlert status="error" title="Chyba při načítání stránky">
      <Button colorScheme="red" isLoading={isLoading} onClick={() => refetch()}>
        <Refresh /> Obnovit stránku
      </Button>
    </PageAlert>
  ) : (
    <SidebarProvider>
      <PageWrapper
        header={
          <>
            <Breadcrumbs items={breadcrumbs} />
            <OrganizationHeader
              organization={organizationData?.data}
              isLoading={isLoading}
            />
          </>
        }
        sidebar={<Sidebar />}
        mainProps={{
          sx: {
            pt: { base: 0, md: 8 },
            gap: 4,
            display: 'grid',
            alignContent: 'baseline',
          },
        }}
      >
        {organizationData && propertiesData && (
          <OrganizationForm
            organization={organizationData?.data}
            properties={propertiesData?.data}
            onSubmit={onSubmit}
            isCreate={false}
          />
        )}
      </PageWrapper>
    </SidebarProvider>
  )
}
